import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@xxtract/ui';
import { useToast } from '@/components/feedback/Toaster';
import { useTheme } from '@/hooks/useTheme';
import { LanguageSelector } from '@/features/auth/components/LanguageSelector';
import { FontSizeSelector } from '@/components/ui/FontSizeSelector';

type ThemeSelectValue = 'system' | 'light' | 'dark';

/**
 * Bundles the EXISTING taal-/thema-/lettergrootte-instellingen (h2-5/h2-6) as one card —
 * this component does not own any of that persistence logic, it only composes the shared
 * pieces (h12-3 anti-pattern guidance: no preferences in localStorage outside the existing
 * mechanism).
 */
export function PreferencesCard() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { theme, setTheme, isSystemTheme, followSystem } = useTheme();

  const themeValue: ThemeSelectValue = isSystemTheme ? 'system' : theme;

  function handleThemeChange(value: string) {
    if (value === 'system') followSystem();
    else setTheme(value as 'light' | 'dark');
    addToast({ key: 'account.preferences.saved' });
  }

  function handleLanguageChange() {
    addToast({ key: 'account.preferences.saved' });
  }

  return (
    <Card data-testid="preferences-card">
      <CardHeader>
        <CardTitle>{t('account.preferences.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5" onChange={handleLanguageChange}>
          <label htmlFor="preferences-language" className="text-xs font-medium text-[var(--foreground-muted)]">
            {t('account.preferences.language')}
          </label>
          <LanguageSelector persistToApi id="preferences-language" />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="preferences-theme" className="text-xs font-medium text-[var(--foreground-muted)]">
            {t('account.preferences.theme')}
          </label>
          <select
            id="preferences-theme"
            data-testid="preferences-theme-select"
            value={themeValue}
            onChange={(e) => handleThemeChange(e.target.value)}
            className="h-9 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)]"
          >
            <option value="system">{t('account.preferences.themeOptions.system')}</option>
            <option value="light">{t('account.preferences.themeOptions.light')}</option>
            <option value="dark">{t('account.preferences.themeOptions.dark')}</option>
          </select>
        </div>

        <div>
          <FontSizeSelector />
        </div>
      </CardContent>
    </Card>
  );
}
